import {
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRODUCT_TYPE_FLASHING_LABEL,
  PRODUCT_TYPE_NOTES_ITEM_LABEL,
  PRODUCT_TYPE_PRODUCT_KIT_LABEL,
  PRODUCT_TYPE_PRODUCT_SUB_KIT_LABEL,
  STATUS,
} from "@/utils/constants";
import order, * as ordersApi from "@/apiv2/order";
import { handleError } from "@/redux/utils/error";
import { calcItemLength, calculateTotalSquareMeters } from "@/redux/modules/helpers/item";
import { getMinLengthProducts } from "@/redux/modules/helpers/product";
import { companySelector } from "@/redux/customers/customers.selectors";

import {
  SET_IS_FETCHING_STATUS,
  SET_PRODUCTS,
  SET_NEW_PRODUCT_DATA,
  SET_IS_SAVING_STATUS,
  SET_IS_CONFIRMATION_MODAL_VISIBLE,
  RESET,
} from "./splitOrders.actionTypes";
import * as selectors from "./splitOrders.selectors";
import { omit } from "lodash";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsFetchingStatus = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_STATUS,
    payload: status,
  });
};

export const setProducts = (data) => (dispatch) => {
  dispatch({
    type: SET_PRODUCTS,
    payload: data,
  });
};

export const setNewProductData = (data) => (dispatch) => {
  dispatch({
    type: SET_NEW_PRODUCT_DATA,
    payload: data,
  });
};

export const setIsSavingStatus = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_SAVING_STATUS,
    payload: status,
  });
};

export const setIsConfirmationModalVisible = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_CONFIRMATION_MODAL_VISIBLE,
    payload: status,
  });
};

export const fetchProducts =
  ({ customerUid, orderUid }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const company = companySelector(state);
      dispatch(setIsFetchingStatus(STATUS.PENDING));
      const { data: products } = await ordersApi.getProductsInAnOrder({ customerUid, orderUid })();
      const { data: drawings } = company.isFlashingEnabled
        ? await ordersApi.getDrawingInAnsOrder({ customerUid, orderUid })()
        : { data: [] };

      const formattedDrawings = drawings.map((d) => ({
        ...d,
        isSelected: false,
        uid: d.item.uid,
        productType: PRODUCT_TYPE_FLASHING_LABEL,
        pricingStrategy: PRICING_STRATEGY_LINEAL_METRES_LABEL,
        productName: `#${d.drawingNumber + 1} ${d.item.productName} ${d.item.color} ${
          d.item.thickness
        }`,
        subitems: d.item.subitems.map((s) => ({
          ...s,
          isDeleted: false,
        })),
      }));
      const formattedProducts = products
        .filter((d) => d.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL)
        .map((d) => {
          const item = {
            ...d,
            isSelected: false,
            originalQuantity: d.quantity,
            subitems: d.subitems.map((s) => ({
              ...s,
              isDeleted: false,
            })),
            productKitItems:
              d.productKitItems?.map((p) => ({
                ...p,
                isSelected: false,
                productKitUid: d.uid,
                lengthItems: p.lengthItems.map((l) => ({
                  ...l,
                  isDeleted: false,
                })),
                originalQuantity: p.quantity,
              })) ?? [],
            subKits:
              d.subKits?.map((s) => ({
                ...s,
                isSelected: false,
                productType: PRODUCT_TYPE_PRODUCT_SUB_KIT_LABEL,
                originalQuantity: s.quantity,
                kitProducts: s.kitProducts.map((k) => ({
                  ...k,
                  isSelected: false,
                  subKitUid: s.uid,
                  originalQuantity: k.quantity,
                  lengthItems: k.lengthItems.map((l) => ({
                    ...l,
                    isDeleted: false,
                  })),
                })),
              })) ?? [],
          };

          if (d.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
            item.sumLengths = calcItemLength(d.subitems, getMinLengthProducts(getState()));
          }

          if (d.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
            item.sumLengths = calculateTotalSquareMeters(d.subitems);
          }

          return item;
        });
      dispatch(setProducts([...formattedDrawings, ...formattedProducts]));

      dispatch(setIsFetchingStatus(STATUS.FULFILLED));
    } catch (error) {
      dispatch(setIsFetchingStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };

export const reformatProductData = () => (dispatch, getState) => {
  const state = getState();
  const products = [...selectors.getProducts(state)].map((p) => {
    const data = { ...p };

    if (data.subKits && data.subKits.length > 0) {
      data.subKits = data.subKits.map((s) => ({
        ...s,
        isSelected: s.kitProducts.filter((k) => k.isSelected).length > 0,
      }));
    }

    if (data.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
      data.isSelected =
        data.subKits.filter((k) => k.isSelected).length > 0 ||
        data.productKitItems.filter((k) => k.isSelected).length > 0;
    }
    return data;
  });

  dispatch(setProducts(products));
};

export const submitOrder =
  ({ customerUid, orderUid }) =>
  async (dispatch, getState) => {
    const state = getState();
    const products = selectors.getProducts(state);
    const items = products
      .filter((p) => p.isSelected)
      .map((p) => {
        const data = {
          uid: p.uid,
        };

        if (p.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
          const totalProductKitItemsCount = p.productKitItems.length;
          const selectedProductKitItems = p.productKitItems.filter((p) => p.isSelected);
          const isAllProductKitItemsSelected =
            selectedProductKitItems.filter((k) => {
              if (
                [
                  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
                  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
                ].includes(k.pricingStrategy)
              ) {
                return parseFloat(k.quantity) === parseFloat(k.originalQuantity);
              } else if (
                [
                  PRICING_STRATEGY_LINEAL_METRES_LABEL,
                  PRICING_STRATEGY_SQUARE_METRES_LABEL,
                ].includes(k.pricingStrategy)
              ) {
                return k.lengthItems.filter((s) => !s.isDeleted).length === k.lengthItems.length;
              }
            }).length === totalProductKitItemsCount;

          const allSubKitItems = p.subKits.flatMap((item) => item.kitProducts);
          const selectedSubKitItems = allSubKitItems.filter((k) => k.isSelected);
          const isAllSubKitItemsSelected =
            selectedSubKitItems.filter((k) => {
              if (
                [
                  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
                  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
                ].includes(k.pricingStrategy)
              ) {
                return parseFloat(k.quantity) === parseFloat(k.originalQuantity);
              } else if (
                [
                  PRICING_STRATEGY_LINEAL_METRES_LABEL,
                  PRICING_STRATEGY_SQUARE_METRES_LABEL,
                ].includes(k.pricingStrategy)
              ) {
                return k.lengthItems.filter((s) => !s.isDeleted).length === k.lengthItems.length;
              }
            }).length === allSubKitItems.length;

          if (isAllSubKitItemsSelected && isAllProductKitItemsSelected) {
            data.move_parent = true;
            return data;
          }

          data.kit_items = p.productKitItems
            .filter((k) => k.isSelected)
            .map((k) => {
              const item = {
                kit_item_uid: k.uid,
              };

              if (
                [
                  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
                  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
                ].includes(k.pricingStrategy)
              ) {
                item.new_item_quantity = parseFloat(k.quantity);
                item.original_item_quantity =
                  parseFloat(k.originalQuantity) - parseFloat(k.quantity);
              }

              if (
                [
                  PRICING_STRATEGY_LINEAL_METRES_LABEL,
                  PRICING_STRATEGY_SQUARE_METRES_LABEL,
                ].includes(k.pricingStrategy)
              ) {
                item.new_item_quantity = k.lengthItems
                  .filter((s) => !s.isDeleted)
                  .map((s) => omit(s, ["isDeleted"]));
                item.original_item_quantity = k.lengthItems
                  .filter((s) => s.isDeleted)
                  .map((s) => omit(s, ["isDeleted"]));
              }

              return item;
            });

          data.sub_kits = p.subKits
            .filter((k) => k.isSelected)
            .map((k) => {
              const subKits = {
                sub_kit_uid: k.uid,
              };

              subKits.sub_kit_items = k.kitProducts
                .filter((k) => k.isSelected)
                .map((k) => {
                  const subKitItems = {
                    sub_kit_item_uid: k.uid,
                  };

                  if (
                    [
                      PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
                      PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
                    ].includes(k.pricingStrategy)
                  ) {
                    subKitItems.new_item_quantity = parseFloat(k.quantity);
                    subKitItems.original_item_quantity =
                      parseFloat(k.originalQuantity) - parseFloat(k.quantity);
                  }

                  if (
                    [
                      PRICING_STRATEGY_LINEAL_METRES_LABEL,
                      PRICING_STRATEGY_SQUARE_METRES_LABEL,
                    ].includes(k.pricingStrategy)
                  ) {
                    subKitItems.new_item_quantity = k.lengthItems
                      .filter((s) => !s.isDeleted)
                      .map((s) => omit(s, ["isDeleted"]));
                    subKitItems.original_item_quantity = k.lengthItems
                      .filter((s) => s.isDeleted)
                      .map((s) => omit(s, ["isDeleted"]));
                  }

                  return subKitItems;
                });
              return subKits;
            });
        }

        if (
          [PRICING_STRATEGY_BASIC_QUANTITIES_LABEL, PRICING_STRATEGY_CUSTOM_FORMULA_LABEL].includes(
            p.pricingStrategy
          )
        ) {
          data.new_item_quantity = parseFloat(p.quantity);
          data.original_item_quantity = parseFloat(p.originalQuantity) - parseFloat(p.quantity);
        }

        if (
          [PRICING_STRATEGY_LINEAL_METRES_LABEL, PRICING_STRATEGY_SQUARE_METRES_LABEL].includes(
            p.pricingStrategy
          )
        ) {
          data.new_item_quantity = p.subitems
            .filter((s) => !s.isDeleted)
            .map((s) => omit(s, ["isDeleted"]));
          data.original_item_quantity = p.subitems
            .filter((s) => s.isDeleted)
            .map((s) => omit(s, ["isDeleted"]));
        }
        return data;
      });

    try {
      dispatch(setIsSavingStatus(STATUS.PENDING));
      await ordersApi.postSplitOrder({ customerUid, orderUid, payload: { items } })();
      dispatch(setIsSavingStatus(STATUS.FULFILLED));
    } catch (error) {
      dispatch(setIsSavingStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };
